import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  Copy, Share2, Download, ExternalLink, MapPin, BadgeCheck, Loader2,
  Linkedin, Facebook, MessageCircle, Mail, Printer, Contact, Maximize2,
  Eye, CalendarCheck, Settings2, QrCode, Trophy, Flame, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import ShareCardPreviewButton from './ShareCardPreviewButton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

/**
 * MyDigitalBusinessCard
 *
 * The same "Live Digital Business Card" featured on the public ievntür
 * website, personalised for the signed-in talent and wired to their
 * EXISTING booking link (`/book/<slug>`). It reuses the public booking
 * profile data (`/booking/public/<slug>`) so photo, name, roles,
 * certifications, markets and rating automatically stay in sync with the
 * talent's profile — no second system, no second booking link.
 *
 * The QR code routes through `/api/booking/card/track/<slug>` so every
 * scan is counted (Scan Insights) before the human lands on the existing
 * public booking page. Free for every talent regardless of subscription.
 *
 * `presentation` renders a full-screen, print-ready variant used by the
 * dedicated "My Card" page (/talent/my-card).
 */

// X (formerly Twitter) glyph — lucide ships no X-brand icon.
const XIcon = ({ className, style }) => (
  <svg viewBox="0 0 24 24" className={className} style={style} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

// WhatsApp glyph — lucide ships no WhatsApp-brand icon.
const WhatsAppIcon = ({ className, style }) => (
  <svg viewBox="0 0 24 24" className={className} style={style} fill="currentColor" aria-hidden="true">
    <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.13h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.11.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.37c0-4.54 3.7-8.23 8.24-8.23 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.69 8.23-8.23 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.79.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.42l-.48-.01c-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.47-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28Z" />
  </svg>
);

// Share milestones that unlock a celebratory badge + toast.
const SHARE_MILESTONES = [10, 50, 100];

const MyDigitalBusinessCard = ({ presentation = false }) => {
  const navigate = useNavigate();
  const [slug, setSlug] = useState(null);
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredSocial, setHoveredSocial] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const linkRes = await api.get('/talent/booking-link');
      const mySlug = linkRes.data?.slug;
      setSlug(mySlug);
      if (mySlug) {
        const pub = await api.get(`/booking/public/${mySlug}`);
        setProfile(pub.data);
      }
      api.get('/talent/card/stats').then((r) => setStats(r.data)).catch(() => {});
    } catch (e) {
      setSlug(null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Card data-testid="digital-business-card-loading">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-cyan-600" />
        </CardContent>
      </Card>
    );
  }
  if (!slug || !profile) return null;

  const origin = window.location.origin;
  const bookingUrl = `${origin}/book/${slug}`;
  // PATH-versioned card URL (Cloudflare ignores query strings) so the preview
  // and shares always reflect the latest design / profile edits, never a stale
  // cached card. `c3` bumps with the card design; the profile timestamp busts
  // the cache when the talent edits their profile.
  const cardVer = (() => {
    const raw = (profile && (profile.updated_at || profile.updatedAt || profile.modified_at)) || '';
    const stamp = String(raw).replace(/[^0-9]/g, '').slice(0, 14);
    return `c3${stamp ? '-' + stamp : ''}`;
  })();
  const cardImageUrl = `${origin}/api/og/v/${cardVer}/card/${slug}.png`;
  // Copy / native / social shares use the `/api/l/:slug` resolver so social
  // crawlers (which don't run JS) get a personalised OG preview card, then
  // real humans are instant-redirected to the clean `/book/:slug` page.
  // `?src=` tracking is forwarded through the resolver to the redirect.
  const shareUrl = `${origin}/api/l/${slug}`;
  // QR routes through the scan tracker → counts the scan → redirects to the
  // existing public booking page. Shared/copied links stay the clean URL.
  const qrTarget = `${origin}/api/booking/card/track/${slug}`;
  const prettyUrl = bookingUrl.replace(/^https?:\/\//, '');
  const shareText = `Book ${profile.display_name || 'me'} directly on iEvntür:`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=0&data=${encodeURIComponent(qrTarget)}`;

  const roles = Array.isArray(profile.gig_roles) ? profile.gig_roles : [];
  // Iter-428 — the talent-chosen primary title headlines the card; if none
  // is set we fall back to their first few gig roles (existing behavior).
  const primaryTitle = (profile.primary_title || '').trim();
  const shownRoles = roles.slice(0, 3);
  const extraRoles = roles.length - shownRoles.length;

  const certs = (Array.isArray(profile.training_certifications) ? profile.training_certifications : [])
    .filter((c) => c && !c.is_expired)
    .map((c) => (c.badge_name || c.module_name || '').trim())
    .filter(Boolean);

  const location = profile.public_location
    || (profile.selected_markets || []).map((m) => m.metro_name).filter(Boolean).join(' & ')
    || [profile.city, profile.state].filter(Boolean).join(', ');

  // Count a share of this card (copy / native / social). Optimistic bump,
  // then reconcile with the server's authoritative all-time total and
  // celebrate when a sharing milestone (10 / 50 / 100) is crossed.
  const recordShare = (network) => {
    const prev = (stats && stats.card_shares) || 0;
    setStats((s) => ({ ...(s || {}), card_shares: (((s && s.card_shares) || 0) + 1) }));
    api.post('/talent/card/share', { network })
      .then((r) => {
        const next = r?.data?.card_shares;
        if (next != null) {
          setStats((s) => ({ ...(s || {}), card_shares: next }));
          const crossed = SHARE_MILESTONES.filter((m) => prev < m && next >= m);
          if (crossed.length) {
            const top = Math.max(...crossed);
            toast.success(`${top} shares reached! Your business card is getting around — keep it up.`);
          }
        }
      })
      .catch(() => {});
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      recordShare('copy');
      toast.success('Booking link copied — paste it anywhere, it shows your photo preview.');
    } catch {
      toast.error('Copy failed');
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'My iEvntür booking card', text: shareText, url: shareUrl });
        recordShare('native');
      } catch { /* user dismissed */ }
    } else {
      copyLink();
    }
  };

  const viewCard = () => window.open(bookingUrl, '_blank', 'noopener,noreferrer');

  const downloadQr = async () => {
    try {
      const res = await fetch(qrSrc);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `ievntur-${slug}-qr.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success('QR code downloaded');
    } catch {
      toast.error('Could not download QR — try again.');
    }
  };

  // Save-to-phone: a vCard (.vcf) the talent can open on their phone to add
  // themselves (name, roles, booking URL) to Contacts / their wallet apps.
  const addToContacts = () => {
    try {
      const name = (profile.display_name || 'iEvntür Talent').trim();
      const parts = name.split(/\s+/);
      const first = parts[0] || name;
      const last = parts.slice(1).join(' ');
      const title = shownRoles.join(', ');
      const lines = ['BEGIN:VCARD', 'VERSION:3.0', `N:${last};${first};;;`, `FN:${name}`, 'ORG:iEvntür'];
      if (title) lines.push(`TITLE:${title}`);
      lines.push(`URL:${bookingUrl}`);
      if (location) lines.push(`ADR;TYPE=work:;;${location};;;;`);
      lines.push('NOTE:Book me directly on iEvntür');
      lines.push('END:VCARD');
      const blob = new Blob([lines.join('\r\n')], { type: 'text/vcard;charset=utf-8' });
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `${slug}.vcf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success('Contact card saved — open it on your phone to add it.');
    } catch {
      toast.error('Could not create contact card — try again.');
    }
  };

  const printCard = () => { try { window.print(); } catch { /* noop */ } };

  // Send the actual branded card as an HTML email (card image shown in the
  // body, clickable to the live booking link) via the server email service.
  const sendCardEmail = async () => {
    const to = emailTo.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      toast.error('Enter a valid recipient email address');
      return;
    }
    setEmailSending(true);
    try {
      const r = await api.post('/talent/card/share-email', {
        recipient_email: to,
        message: emailMsg.trim() || undefined,
      });
      const next = r?.data?.card_shares;
      if (next != null) setStats((s) => ({ ...(s || {}), card_shares: next }));
      toast.success(`Your business card was emailed to ${to}`);
      setEmailOpen(false);
      setEmailTo('');
      setEmailMsg('');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not send the email. Please try again.';
      toast.error(detail);
    } finally {
      setEmailSending(false);
    }
  };

  const openShare = (url) => window.open(url, '_blank', 'noopener,noreferrer,width=600,height=600');
  // Tag each shared link with its traffic source so the talent's analytics
  // can attribute profile views back to LinkedIn / Facebook / Instagram etc.
  const tagged = (network) => `${shareUrl}?src=${network}`;
  const socials = [
    { key: 'linkedin', label: 'LinkedIn', Icon: Linkedin, onClick: () => { recordShare('linkedin'); openShare(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(tagged('linkedin'))}`); } },
    { key: 'facebook', label: 'Facebook', Icon: Facebook, onClick: () => { recordShare('facebook'); openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(tagged('facebook'))}`); } },
    { key: 'x', label: 'X', Icon: XIcon, fillGlyph: true, onClick: () => { recordShare('x'); openShare(`https://twitter.com/intent/tweet?url=${encodeURIComponent(tagged('x'))}&text=${encodeURIComponent(shareText)}`); } },
    { key: 'whatsapp', label: 'WhatsApp', Icon: WhatsAppIcon, fillGlyph: true, onClick: () => { recordShare('whatsapp'); openShare(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + tagged('whatsapp'))}`); } },
    { key: 'message', label: 'Message', Icon: MessageCircle, isLink: true, onClick: () => recordShare('message'), href: `sms:?&body=${encodeURIComponent(shareText + ' ' + tagged('message'))}` },
    { key: 'email', label: 'Email', Icon: Mail, onClick: () => { setEmailTo(''); setEmailMsg(''); setEmailOpen(true); } },
  ];
  const MUSTARD = '#D4A017'; // reserved for the share-milestone trophy badge
  // Blue→turquoise gradient matching the QR frame, applied to BOTH the circular
  // ring (gradient border via layered backgrounds) and the glyph itself
  // (SVG stroke/fill → url(#dbc-brand-grad)).
  const gradientBorderStyle = {
    border: '2px solid transparent',
    backgroundImage: 'linear-gradient(#fff,#fff), linear-gradient(135deg,#1D4ED8 0%,#2563EB 45%,#06B6D4 100%)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
  };
  const socialBtnClass = 'w-full aspect-square rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 hover:-translate-y-0.5 hover:shadow-md';

  const initials = (profile.display_name || 'ME').split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase();

  // Highest share milestone reached (for the celebratory badge).
  const shareCount = stats?.card_shares ?? 0;
  const shareMilestone = [...SHARE_MILESTONES].reverse().find((m) => shareCount >= m);
  // Consecutive-day share streak (reward daily promotion).
  const streakDays = stats?.share_streak_days ?? 0;
  // Which share channel drives the most card views.
  const CHANNEL_LABELS = {
    linkedin: 'LinkedIn', facebook: 'Facebook', x: 'X', whatsapp: 'WhatsApp',
    message: 'Messages', email: 'Email', copy: 'Copied link', native: 'Share sheet',
  };
  const topChannel = stats?.top_share_channel || null;
  const topChannelLabel = topChannel ? (CHANNEL_LABELS[topChannel.source] || topChannel.source) : null;

  const card = (
    <Card className="overflow-hidden border-gray-200 shadow-sm dbc-print-area" data-testid="digital-business-card">
      <CardContent className={presentation ? 'p-6 sm:p-8' : 'p-5 sm:p-6'}>
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <span className="text-xs sm:text-sm font-bold tracking-[0.12em] text-cyan-600 uppercase" data-testid="dbc-title">
            Live Digital Business Card
          </span>
        </div>

        {/* Identity */}
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-full ring-2 ring-cyan-200 ring-offset-2 overflow-hidden bg-cyan-50 flex items-center justify-center">
            {profile.profile_photo_url ? (
              <img
                src={profile.profile_photo_url}
                alt={profile.display_name}
                className="w-full h-full object-cover"
                data-testid="dbc-photo"
              />
            ) : (
              <span className="text-xl font-bold text-cyan-700" data-testid="dbc-photo-fallback">{initials}</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 truncate" data-testid="dbc-name">
                {profile.display_name}
              </h3>
              {profile.verified && (
                <BadgeCheck className="w-5 h-5 text-cyan-500 shrink-0" data-testid="dbc-verified" />
              )}
            </div>
            {primaryTitle && (
              <p className="text-sm sm:text-base font-semibold text-cyan-600 mt-1 leading-snug" data-testid="dbc-primary-title">
                {primaryTitle}
              </p>
            )}
            {shownRoles.length > 0 && (
              <p className={`text-sm sm:text-base text-gray-700 leading-snug ${primaryTitle ? 'mt-0.5' : 'mt-1'}`} data-testid="dbc-roles">
                {shownRoles.join(' · ')}
                {extraRoles > 0 && <span className="text-gray-500"> +{extraRoles} more</span>}
              </p>
            )}
            {location && (
              <p className="flex items-start gap-1 text-sm text-gray-500 mt-1.5" data-testid="dbc-location">
                <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{location}</span>
              </p>
            )}
          </div>
        </div>

        {/* Certifications */}
        {certs.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4" data-testid="dbc-certs">
            {certs.map((c, i) => (
              <span
                key={i}
                className="text-sm font-semibold text-cyan-700 bg-cyan-50 border border-cyan-100 rounded-lg px-3 py-1.5"
                data-testid="dbc-cert-chip"
              >
                {/Certified|Certification/i.test(c) ? c : `${c} Certified`}
              </span>
            ))}
          </div>
        )}

        {/* QR code */}
        <div className="flex flex-col items-center my-6">
          <div
            className="rounded-2xl p-[5px] shadow-lg"
            style={{ background: 'linear-gradient(135deg, #1D4ED8 0%, #2563EB 45%, #06B6D4 100%)', boxShadow: '0 8px 22px -6px rgba(29,78,216,0.45)' }}
          >
            <div className="p-3 bg-white rounded-xl">
              <img
                src={qrSrc}
                alt="Booking QR code"
                className={presentation ? 'w-52 h-52 sm:w-60 sm:h-60' : 'w-40 h-40 sm:w-44 sm:h-44'}
                data-testid="dbc-qr"
              />
            </div>
          </div>
          <span className="text-sm text-gray-400 mt-2">Scan to book</span>
        </div>

        {/* Booking link + copy */}
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl p-2 pl-4" data-testid="dbc-link-row">
          <code className="flex-1 text-sm text-gray-700 truncate">{prettyUrl}</code>
          <Button
            onClick={copyLink}
            className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg shrink-0 dbc-no-print"
            data-testid="dbc-copy-link"
          >
            <Copy className="w-4 h-4 mr-1.5" /> Copy Link
          </Button>
        </div>

        {/* Scan insights */}
        <div
          className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 mt-3 text-sm text-gray-600 dbc-no-print"
          data-testid="dbc-scan-insights"
        >
          <span className="inline-flex items-center gap-1.5">
            <Eye className="w-4 h-4 text-cyan-500" />
            <strong className="text-gray-900" data-testid="dbc-view-count">{stats?.profile_views ?? 0}</strong> views
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Share2 className="w-4 h-4 text-teal-500" />
            <strong className="text-gray-900" data-testid="dbc-share-count">{stats?.card_shares ?? 0}</strong> shares
          </span>
          <span className="inline-flex items-center gap-1.5">
            <QrCode className="w-4 h-4 text-sky-500" />
            <strong className="text-gray-900" data-testid="dbc-scan-count">{stats?.scans ?? 0}</strong> scans
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CalendarCheck className="w-4 h-4 text-emerald-500" />
            <strong className="text-gray-900" data-testid="dbc-booking-count">{stats?.bookings_from_card ?? 0}</strong> bookings
          </span>
        </div>

        {/* Share milestone + streak celebration badges */}
        {(shareMilestone || streakDays >= 2) && (
          <div className="flex flex-wrap justify-center gap-2 mt-2 dbc-no-print">
            {shareMilestone && (
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                style={{ color: '#8A6D1A', backgroundColor: 'rgba(212,160,23,0.14)', border: '1px solid rgba(212,160,23,0.45)' }}
                data-testid="dbc-share-milestone"
              >
                <Trophy className="w-3.5 h-3.5" style={{ color: MUSTARD }} />
                {shareMilestone}+ shares — you're on a roll!
              </span>
            )}
            {streakDays >= 2 && (
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                style={{ color: '#9A3412', backgroundColor: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.4)' }}
                data-testid="dbc-share-streak"
              >
                <Flame className="w-3.5 h-3.5" style={{ color: '#F97316' }} />
                {streakDays}-day sharing streak
              </span>
            )}
          </div>
        )}

        {/* Top channel insight — where shares convert to views */}
        {topChannel && (
          <div className="flex justify-center mt-2 dbc-no-print" data-testid="dbc-top-channel">
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
              <TrendingUp className="w-3.5 h-3.5 text-cyan-500" />
              <span className="text-gray-700 font-semibold">{topChannelLabel}</span>
              drives your most views
              <span className="text-gray-400">({topChannel.views})</span>
            </span>
          </div>
        )}

        {/* Primary actions */}
        <div className="grid grid-cols-3 gap-2 mt-3 dbc-no-print">
          <Button variant="outline" onClick={viewCard} className="rounded-xl" data-testid="dbc-view">
            <ExternalLink className="w-4 h-4 mr-1.5" /> View
          </Button>
          <Button variant="outline" onClick={share} className="rounded-xl" data-testid="dbc-share">
            <Share2 className="w-4 h-4 mr-1.5" /> Share
          </Button>
          <Button variant="outline" onClick={downloadQr} className="rounded-xl" data-testid="dbc-download-qr">
            <Download className="w-4 h-4 mr-1.5" /> QR
          </Button>
        </div>

        {/* Secondary actions — Present/Print + save to phone */}
        <div className="grid grid-cols-2 gap-2 mt-2 dbc-no-print">
          {presentation ? (
            <Button variant="outline" onClick={printCard} className="rounded-xl" data-testid="dbc-print">
              <Printer className="w-4 h-4 mr-1.5" /> Print / PDF
            </Button>
          ) : (
            <Button variant="outline" onClick={() => navigate('/talent/my-card')} className="rounded-xl" data-testid="dbc-present">
              <Maximize2 className="w-4 h-4 mr-1.5" /> Full Screen
            </Button>
          )}
          <Button variant="outline" onClick={addToContacts} className="rounded-xl" data-testid="dbc-add-contact">
            <Contact className="w-4 h-4 mr-1.5" /> Add to Contacts
          </Button>
        </div>

        {/* Preview the exact social/share card people see on Facebook etc. */}
        <div className="mt-2 dbc-no-print">
          <ShareCardPreviewButton
            imageUrl={cardImageUrl}
            className="w-full"
            fileName={`ievntur-${slug}-share-card.png`}
            testid="dbc-share-card-preview"
          />
        </div>

        {/* Social share */}
        <div className="mt-5 dbc-no-print">
          <span className="block text-sm font-medium text-gray-500 mb-2">Share on</span>
          {/* Brand gradient referenced by the icon glyphs below. */}
          <svg width="0" height="0" className="absolute" aria-hidden="true">
            <defs>
              <linearGradient id="dbc-brand-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#1D4ED8" />
                <stop offset="45%" stopColor="#2563EB" />
                <stop offset="100%" stopColor="#06B6D4" />
              </linearGradient>
            </defs>
          </svg>
          <div className="grid grid-cols-6 gap-2">
            {socials.map(({ key, label, Icon, onClick, isLink, href, fillGlyph }) => {
              const isHovered = hoveredSocial === key;
              // Hover: fill the circle solid with the brand gradient and flip
              // the glyph to white for a crisp, unified pop.
              const btnStyle = isHovered
                ? { border: '2px solid transparent', backgroundImage: 'linear-gradient(135deg,#1D4ED8 0%,#2563EB 45%,#06B6D4 100%)' }
                : gradientBorderStyle;
              const iconStyle = isHovered
                ? (fillGlyph ? { fill: '#fff' } : { stroke: '#fff' })
                : (fillGlyph ? { fill: 'url(#dbc-brand-grad)' } : { stroke: 'url(#dbc-brand-grad)' });
              const hoverHandlers = {
                onMouseEnter: () => setHoveredSocial(key),
                onMouseLeave: () => setHoveredSocial((k) => (k === key ? null : k)),
                onFocus: () => setHoveredSocial(key),
                onBlur: () => setHoveredSocial((k) => (k === key ? null : k)),
              };
              const inner = <Icon className="w-[18px] h-[18px]" style={iconStyle} />;
              return isLink ? (
                <a
                  key={key}
                  href={href}
                  onClick={onClick}
                  {...hoverHandlers}
                  aria-label={label}
                  title={label}
                  className={socialBtnClass}
                  style={btnStyle}
                  data-testid={`dbc-social-${key}`}
                >
                  {inner}
                </a>
              ) : (
                <button
                  key={key}
                  onClick={onClick}
                  {...hoverHandlers}
                  aria-label={label}
                  title={label}
                  className={socialBtnClass}
                  style={btnStyle}
                  data-testid={`dbc-social-${key}`}
                >
                  {inner}
                </button>
              );
            })}
          </div>
        </div>

        {/* Share-by-email dialog — sends the branded card image in the body */}
        <Dialog open={emailOpen} onOpenChange={(o) => { if (!emailSending) setEmailOpen(o); }}>
          <DialogContent className="sm:max-w-md dbc-no-print" data-testid="dbc-email-dialog">
            <DialogHeader>
              <DialogTitle>Email my business card</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                We'll email your actual digital business card — the recipient sees the card itself, tappable to your live booking page.
              </p>
              <div>
                <label className="text-xs font-medium text-gray-600" htmlFor="dbc-email-to">Recipient email</label>
                <Input
                  id="dbc-email-to"
                  type="email"
                  placeholder="name@example.com"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  disabled={emailSending}
                  data-testid="dbc-email-to-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600" htmlFor="dbc-email-msg">Personal note (optional)</label>
                <Textarea
                  id="dbc-email-msg"
                  rows={3}
                  maxLength={500}
                  placeholder="Add a short message…"
                  value={emailMsg}
                  onChange={(e) => setEmailMsg(e.target.value)}
                  disabled={emailSending}
                  data-testid="dbc-email-msg-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailOpen(false)} disabled={emailSending} data-testid="dbc-email-cancel">
                Cancel
              </Button>
              <Button onClick={sendCardEmail} disabled={emailSending} data-testid="dbc-email-send">
                {emailSending ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</>) : (<><Mail className="w-4 h-4 mr-1.5" /> Send card</>)}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manage custom URL — lives in Profile Settings */}
        <button
          onClick={() => navigate('/talent/settings?section=booking-link')}
          className="mt-4 w-full text-xs text-gray-500 hover:text-cyan-600 inline-flex items-center justify-center gap-1.5 transition-colors dbc-no-print"
          data-testid="dbc-manage-link"
        >
          <Settings2 className="w-3.5 h-3.5" /> Manage booking link &amp; custom URL
        </button>
      </CardContent>
    </Card>
  );

  if (!presentation) return card;

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .dbc-print-area, .dbc-print-area * { visibility: visible !important; }
          .dbc-print-area { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; border: none !important; }
          .dbc-no-print { display: none !important; }
        }
      `}</style>
      {card}
    </>
  );
};

export default MyDigitalBusinessCard;
